"use client";

import React from "react";

/**
 * A single entry rendered in the master (sidebar) list. `leading` is a fully
 * rendered icon node so each editor can supply its own themed icon (credential
 * category tone, offering icon, etc.) without the shared component knowing about
 * domain-specific icon systems.
 */
export type ProfileMasterDetailEntry = Readonly<{
  id: string;
  title: string;
  subtitle?: string;
  leading?: React.ReactNode;
  ariaLabel?: string;
  componentInstance?: string;
}>;

/**
 * Shared "tile editor" experience: an optional filter row pinned to the top, a
 * master list (sidebar) with an add button, and a detail panel. This is the one
 * canonical shell for every master-detail profile editor popup (Credentials &
 * Waivers, Offerings, Service Conditions). Each editor instantiates it with its
 * own entries, detail form (`children`), and optional filter row/footer, so the
 * layout and spacing stay identical while the contents vary.
 */
export type ProfileMasterDetailEditorProps = Readonly<{
  componentInstance?: string;
  className?: string;
  /** Optional filter row (e.g. a ProfileFilterChipGroup) rendered above the columns. */
  filters?: React.ReactNode;
  addLabel: string;
  onAdd: () => void;
  addDisabled?: boolean;
  entries: readonly ProfileMasterDetailEntry[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Detail panel content. Callers render their own empty state within children. */
  children: React.ReactNode;
}>;

export function ProfileMasterDetailEditor({
  componentInstance,
  className,
  filters,
  addLabel,
  onAdd,
  addDisabled,
  entries,
  selectedId,
  onSelect,
  children,
}: ProfileMasterDetailEditorProps) {
  return (
    <div
      className={["profile-master-detail-editor", className].filter(Boolean).join(" ")}
      data-component-instance={componentInstance}
    >
      {filters ? (
        <div className="profile-master-detail-editor__filters">{filters}</div>
      ) : null}
      <div className="profile-master-detail-editor__body">
        <div className="profile-master-detail-editor__sidebar">
          <button
            type="button"
            className="profile-master-detail-editor__add"
            onClick={onAdd}
            disabled={addDisabled}
          >
            <span aria-hidden="true">+</span>
            {addLabel}
          </button>
          <ul className="dj-selection-card-list profile-master-detail-editor__sidebar-list">
            {entries.map((entry) => {
              const isActive = entry.id === selectedId;
              return (
                <li key={entry.id}>
                  <button
                    type="button"
                    className={[
                      "dj-selection-card",
                      isActive ? "dj-selection-card--active" : "",
                    ].filter(Boolean).join(" ")}
                    aria-label={entry.ariaLabel ?? entry.title}
                    aria-current={isActive ? "true" : undefined}
                    data-component-instance={entry.componentInstance}
                    onClick={() => onSelect(entry.id)}
                  >
                    {entry.leading}
                    <span className="dj-selection-card__copy">
                      <strong className="dj-selection-card__title">{entry.title}</strong>
                      {entry.subtitle ? (
                        <span className="dj-selection-card__subtitle">{entry.subtitle}</span>
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="profile-master-detail-editor__panel">{children}</div>
      </div>
    </div>
  );
}
